import { usePage } from '@inertiajs/react'
import { Link } from '@inertiajs/react'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { t } from '@/shared/lib/i18n'
import {
  complianceDashboardPath,
  compliancePdfPath,
  historyInstrumentTaskTaskLogPath,
  instrumentPath,
} from '@/shared/lib/routes'
import { formatCalendarDate } from '@/shared/lib/tenantDateTime'
import type { SharedProps } from '@/shared/types/SharedProps'
import { NonComplianceCard } from '@/features/non-compliance-actions/NonComplianceCard'
import type { WebComplianceDetail, WebComplianceReviewList } from '@/shared/types/web'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'
import { useCan } from '@/shared/hooks/useCan'
import { Actions, Resources } from '@/shared/rbac'

interface Props {
  record: WebComplianceDetail
  recordCard: WebComplianceReviewList
  [key: string]: unknown
}

const complianceTypeLabel = (type: string | null): string => {
  if (!type) return '—'
  const key = type.toLowerCase()
  if (key === 'overdue') return t('compliance.detail.compliance_type_overdue')
  if (key === 'scheduled') return t('compliance.detail.compliance_type_scheduled')
  if (key === 'error') return t('compliance.detail.compliance_type_error')
  return type
}

const ComplianceShowPage = () => {
  const { formatDateTime } = useSiteDateTime()
  const { record, recordCard, taskHistoryEnabled } = usePage<Props & SharedProps>().props
  const canReview = useCan(Resources.COMPLIANCE_REPORT, Actions.UPDATE)
  const pendingReview = record.reviewStatusLabel === t('compliance.dashboard.pending')

  useBreadcrumbs([
    { label: t('nav.dashboard'), href: complianceDashboardPath() },
    { label: t('nav.complianceReviews'), href: complianceDashboardPath() },
    { label: record.instrumentLabel },
  ])

  const reviewedAtLabel = record.reviewStatusRecordedAt
    ? `${formatDateTime(record.reviewStatusRecordedAt)} ${record.reviewStatusRecordedAtZoneAbbreviation ?? ''}`.trim()
    : null

  return (
    <div className="space-y-8 max-w-3xl">
      <div className="flex items-center justify-between">
        <Button variant="outline" asChild>
          <a href={compliancePdfPath({ id: record.id })} download>
            {t('compliance.detail.export_pdf')}
          </a>
        </Button>
      </div>

      <h1 className="text-2xl font-bold">{t('compliance.detail.title')}</h1>

      <section className="rounded-md border p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">{t('compliance.dashboard.table.date')}: </span>
            <span>{formatCalendarDate(record.date)}</span>
          </div>
          <div>
            <span className="text-muted-foreground">
              {t('compliance.dashboard.table.instrument')}:{' '}
            </span>
            <Link
              href={instrumentPath(record.instrumentId)}
              className="text-primary underline-offset-4 hover:underline"
            >
              {record.instrumentLabel}
            </Link>
          </div>
          <div>
            <Link
              href={record.viewMaintenanceLogsHref}
              className="text-sm text-primary underline-offset-4 hover:underline"
            >
              {t('compliance.detail.viewMaintenanceLogs')}
            </Link>
          </div>
          <div>
            <span className="text-muted-foreground">{t('compliance.detail.serialNumber')}: </span>
            <span>{record.serialNumber ?? '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">{t('compliance.detail.ellId')}: </span>
            <span>{record.instrumentId}</span>
          </div>
          <div>
            <span className="text-muted-foreground">{t('compliance.dashboard.table.type')}: </span>
            <span>{record.instrumentTypeName ?? '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">
              {t('compliance.dashboard.table.department')}:{' '}
            </span>
            <span>
              {record.departmentName}
              {record.unitName ? ` / ${record.unitName}` : ''}
            </span>
          </div>
          {record.siteName && (
            <div>
              <span className="text-muted-foreground">
                {t('compliance.dashboard.table.site')}:{' '}
              </span>
              <span>{record.siteName}</span>
            </div>
          )}
          {record.complianceType && (
            <div>
              <Badge variant="outline">{complianceTypeLabel(record.complianceType)}</Badge>
            </div>
          )}
        </div>
      </section>

      <NonComplianceCard
        record={recordCard}
        hideActions={!pendingReview}
        allowOfflineRetire={false}
        canReview={canReview}
        selectMode={false}
        selected={false}
        isDismissing={false}
        onActionStart={() => {}}
        onToggleSelect={() => {}}
      />

      <section className="rounded-md border p-4 space-y-4">
        <h2 className="font-semibold text-lg">{t('compliance.detail.review_section_title')}</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">
              {t('compliance.detail.review_status_label')}:{' '}
            </span>
            <span>{record.reviewStatusLabel ?? '—'}</span>
          </div>
          {record.resolutionLabel && (
            <div>
              <span className="text-muted-foreground">
                {t('compliance.dashboard.table.resolutionType')}:{' '}
              </span>
              <span>{record.resolutionLabel}</span>
            </div>
          )}
          {record.reviewStatusRecordedByUserName && (
            <div>
              <span className="text-muted-foreground">
                {t('compliance.detail.reviewer_label')}:{' '}
              </span>
              <span>{record.reviewStatusRecordedByUserName}</span>
            </div>
          )}
          {reviewedAtLabel && (
            <div>
              <span className="text-muted-foreground">
                {t('compliance.detail.reviewed_at_label')}:{' '}
              </span>
              <span>{reviewedAtLabel}</span>
            </div>
          )}
        </div>
      </section>

      <section className="rounded-md border p-4 space-y-3">
        <h2 className="font-semibold text-lg">{t('compliance.detail.task_section_title')}</h2>
        {record.instrumentTaskId ? (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">
                {t('compliance.detail.task_name_label')}:{' '}
              </span>
              <span>{record.instrumentTaskName ?? '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">
                {t('compliance.detail.interval_type_label')}:{' '}
              </span>
              <span>{record.intervalTypeName ?? '—'}</span>
            </div>
            {taskHistoryEnabled && (
              <div>
                <Link
                  href={historyInstrumentTaskTaskLogPath(
                    record.instrumentId,
                    record.instrumentTaskId!,
                  )}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {t('compliance.detail.view_task_history')}
                </Link>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('compliance.detail.no_task')}</p>
        )}
      </section>
    </div>
  )
}

export default ComplianceShowPage
